import { validatePortalPassword } from "./accept-invite.js";
import { getSupabase } from "./supabase.js";

/* Mensagem única para e-mail existente e inexistente.
   Confirmar que um endereço tem conta seria enumeração de usuários — o atacante
   descobriria quem trabalha no cliente só testando endereços no formulário. */
export const RECOVERY_SENT_MESSAGE =
  "Se este e-mail tiver acesso ao portal, enviamos um link de redefinição. Confira a caixa de entrada e o spam.";

export function createPasswordResetRequestController({ resetPasswordForEmail }) {
  return {
    async submit({ email }) {
      const address = String(email || "").trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
        throw new Error("Informe um e-mail válido.");
      }
      /* Erro do provedor é engolido de propósito: qualquer diferença de resposta
         entre e-mail conhecido e desconhecido vazaria a existência da conta. */
      try {
        await resetPasswordForEmail(address);
      } catch {
        /* silêncio intencional */
      }
      return RECOVERY_SENT_MESSAGE;
    },
  };
}

export function createPasswordUpdateController({ getSession, updateUser, signOut, navigate = () => {} }) {
  return {
    async initialize() {
      const { data, error } = await getSession();
      if (error || !data?.session?.user) {
        throw new Error("Este link de redefinição é inválido ou expirou.");
      }
      return data.session.user;
    },
    async submit({ password, confirmation }) {
      const validPassword = validatePortalPassword(password, confirmation);
      const { data, error } = await updateUser({ password: validPassword });
      if (error || !data?.user) {
        throw new Error("Não foi possível redefinir a senha. Solicite um novo link.");
      }
      /* Escopo global, diferente do convite: redefinir senha implica possível
         comprometimento, então toda sessão aberta em qualquer aparelho deve cair. */
      await signOut({ scope: "global" });
      navigate("index.html?senha=redefinida");
    },
  };
}

export function mountPasswordResetRequest({
  document: documentRef = document,
  client,
} = {}) {
  const form = documentRef.getElementById("recoveryForm");
  if (!form) return null;
  const supabase = client || getSupabase();
  const status = documentRef.getElementById("recoveryStatus");
  const emailInput = documentRef.getElementById("recoveryEmail");
  const submit = documentRef.getElementById("recoverySubmit");

  const controller = createPasswordResetRequestController({
    resetPasswordForEmail: (email) => supabase.auth.resetPasswordForEmail(email, {
      redirectTo: new URL("redefinir-senha.html", window.location.href).toString(),
    }),
  });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (submit) submit.disabled = true;
    if (status) status.classList.remove("is-error");
    try {
      const message = await controller.submit({ email: emailInput?.value });
      if (status) status.textContent = message;
      form.reset();
    } catch (error) {
      if (status) {
        status.textContent = error?.message || "Não foi possível enviar o link.";
        status.classList.add("is-error");
      }
    } finally {
      if (submit) submit.disabled = false;
    }
  });

  return { controller };
}

export function mountPasswordUpdate({
  document: documentRef = document,
  client,
  navigate = (target) => window.location.replace(target),
} = {}) {
  const form = documentRef.getElementById("passwordUpdateForm");
  if (!form) return null;
  const supabase = client || getSupabase();
  const status = documentRef.getElementById("passwordStatus");
  const passwordInput = documentRef.getElementById("newPassword");
  const confirmationInput = documentRef.getElementById("newPasswordConfirmation");
  const submit = documentRef.getElementById("passwordSubmit");

  const controller = createPasswordUpdateController({
    getSession: () => supabase.auth.getSession(),
    updateUser: (attributes) => supabase.auth.updateUser(attributes),
    signOut: (options) => supabase.auth.signOut(options),
    navigate,
  });

  controller.initialize()
    .then(() => {
      if (status) status.textContent = "Link confirmado. Defina a nova senha.";
    })
    .catch(() => {
      if (status) {
        status.textContent = "Este link de redefinição é inválido ou expirou. Solicite um novo.";
        status.classList.add("is-error");
      }
      form.hidden = true;
    });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (submit) submit.disabled = true;
    if (status) status.classList.remove("is-error");
    try {
      await controller.submit({
        password: passwordInput?.value,
        confirmation: confirmationInput?.value,
      });
    } catch (error) {
      if (status) {
        status.textContent = error?.message || "Não foi possível redefinir a senha.";
        status.classList.add("is-error");
      }
      if (submit) submit.disabled = false;
    }
  });

  return { controller };
}

if (typeof document !== "undefined") {
  mountPasswordResetRequest();
  mountPasswordUpdate();
}
